'use client';

import { CaretLeft, CaretRight } from '@phosphor-icons/react/ssr';
import { format } from 'date-fns';
import * as React from 'react';
import {
  type DayButton,
  DayPicker,
  type DayPickerProps,
  type NavProps,
  type RootProps,
} from 'react-day-picker';

import { cn } from '../../lib/utils';
import { dayCellState, dayCellStyles } from '../lib/calendar-styles';
import { IconButton } from '../lib/icon-button';

/**
 * shadcn Calendar (react-day-picker), restyled to the Figma Date Picker
 * (62:342): Monday first, Label/Large month caption with ghost Small
 * caret Icon Buttons on the right, Caption text/tertiary weekdays in 40×28
 * cells, 40×40 Day Cells (62:33). Owned code - apps use `Calendar` from
 * components/lib.
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  weekStartsOn = 1,
  labels,
  components,
  ...props
}: DayPickerProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      weekStartsOn={weekStartsOn}
      className={cn('w-fit', className)}
      classNames={{
        months: 'relative flex gap-6',
        month: 'flex flex-col gap-2',
        nav: 'absolute top-0 right-0 flex',
        month_caption: 'flex h-8 items-center pr-16 pl-1.5',
        caption_label: 'text-label-lg text-text-primary',
        month_grid: 'flex flex-col gap-2',
        weekdays: 'flex',
        weekday:
          'text-caption text-text-tertiary flex h-7 w-10 items-center justify-center p-0',
        weeks: 'flex flex-col',
        week: 'flex',
        day: 'size-10 p-0',
        hidden: 'invisible',
        ...classNames,
      }}
      labels={{
        labelDayButton: (date, m) =>
          [
            format(date, 'EEEE, d MMMM yyyy'),
            m.today && 'today',
            m.selected && 'selected',
            m.disabled && 'unavailable',
          ]
            .filter(Boolean)
            .join(', '),
        ...labels,
      }}
      components={{
        Root: CalendarRoot,
        Nav: CalendarNav,
        DayButton: CalendarDayButton,
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarRoot({ rootRef, ...props }: RootProps) {
  return <div data-slot="calendar" ref={rootRef} {...props} />;
}

/**
 * Previous / next month as Figma Icon Buttons (Size=Small, Style=Ghost). A
 * named group rather than rdp's <nav>: two calendars on a page would repeat
 * the landmark.
 */
function CalendarNav({
  onPreviousClick,
  onNextClick,
  previousMonth,
  nextMonth,
  ...props
}: NavProps) {
  return (
    <div {...props} role="group" aria-label="Month navigation">
      <IconButton
        icon={CaretLeft}
        size="sm"
        aria-label="Previous month"
        aria-disabled={!previousMonth || undefined}
        onClick={(e) => previousMonth && onPreviousClick?.(e)}
      />
      <IconButton
        icon={CaretRight}
        size="sm"
        aria-label="Next month"
        aria-disabled={!nextMonth || undefined}
        onClick={(e) => nextMonth && onNextClick?.(e)}
      />
    </div>
  );
}

/** The Figma Day Cell for one react-day-picker day: state from its modifiers. */
function CalendarDayButton({
  className,
  day: _day,
  modifiers,
  children,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);
  const state = dayCellState(modifiers);
  const styles = dayCellStyles({ state });
  return (
    <button
      ref={ref}
      data-slot="day-cell"
      data-cell-state={state}
      className={styles.root({ className })}
      {...props}
    >
      <span aria-hidden className={styles.band()} />
      <span
        data-slot="day-cell-chip"
        data-disabled={state === 'disabled' || undefined}
        className={styles.chip()}
      >
        {children}
      </span>
    </button>
  );
}

export { Calendar, CalendarDayButton };
